import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Clock, DownloadIcon, FolderArchive, Youtube } from 'lucide-react';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { authQueries } from 'src/api/queries';
import { Button } from 'src/components/ui/button/button';
import { Href } from 'src/components/ui/href';
import { mediaClient } from 'src/lib/services/api/media-api';
import { useFormatter } from 'src/services/i18n';

const ArchivedMediaMessage: React.FC<{ mediaId: string; requestId: string }> = ({
  mediaId,
  requestId,
}) => {
  const { t } = useTranslation();
  const client = useQueryClient();

  const restoreMutation = useMutation({
    mutationFn: (args: Parameters<typeof mediaClient.restore>) => mediaClient.restore(...args),
  });

  const restoreMedia = async () => {
    await restoreMutation.mutateAsync([mediaId], {
      onSuccess: () => {
        client.invalidateQueries({
          queryKey: [authQueries.request, requestId],
        });
      },
    });
  };

  return (
    <div
      className={
        'tw-flex tw-aspect-video tw-h-full tw-items-center tw-justify-center tw-rounded-md tw-bg-neutral-50 tw-text-text-strong'
      }
    >
      <div className={'tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4 tw-p-4'}>
        <FolderArchive size={80} className={'tw-text-text-inactive'} />
        <p className={'tw-font-medium'}>{t('This file is archived due to inactivity')}</p>
        <Button onClick={restoreMedia} variant={'brand'} size={'responsive'}>
          {t('Unarchive')}
        </Button>
      </div>
    </div>
  );
};

const UnarchivedMediaMessage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div
      className={
        'tw-flex tw-aspect-video tw-h-full tw-items-center tw-justify-center tw-rounded-md tw-bg-neutral-50 tw-text-text-strong'
      }
    >
      <div className={'tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4 tw-p-4'}>
        <Clock size={80} className={'tw-text-text-inactive'} />
        <p className={'tw-font-bold'}>{t('Please wait..')}</p>
        <p className={'tw-max-w-64 tw-text-center tw-text-text-inactive'}>
          {t('System is currently unarchiving your file (it might take up to 24 hours)')}
        </p>
      </div>
    </div>
  );
};

const ReadyToDownloadMediaMessage: React.FC<{
  expiryDate: string;
  downloadUrl: string;
}> = ({ expiryDate, downloadUrl }) => {
  const { t } = useTranslation();
  const { formatDate } = useFormatter();

  return (
    <div
      className={
        'tw-flex tw-aspect-video tw-h-full tw-items-center tw-justify-center tw-rounded-md tw-bg-neutral-50 tw-text-text-strong'
      }
    >
      <div className={'tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4 tw-p-4'}>
        <Youtube size={80} className={'tw-text-text-inactive'} />
        <p className={'tw-font-bold'}>{t('Your file was unarchived')}</p>
        <p className={'tw-text-text-inactive'}>
          <Trans>
            <span>
              You have {formatDate(expiryDate)} to download your content, until it’s archived.
            </span>
          </Trans>
        </p>
        <Button asChild variant={'brand'} size={'iconStartResponsive'}>
          <Href href={downloadUrl} target={'_blank'} variant={'unset'}>
            <DownloadIcon size={20} />
            <span>{t('Download')}</span>
          </Href>
        </Button>
      </div>
    </div>
  );
};

export { ArchivedMediaMessage, UnarchivedMediaMessage, ReadyToDownloadMediaMessage };
